/**
 * AN INVOICE THE HOUSEHOLD'S SAVED METHOD PAYS, on its own page, in place of the ways to pay: ops
 * offers no bank and no card then, and refuses them (the seam, section 5). No artboard draws it
 * (business issue 13), so it is built from drawn parts: the saved method's white card on Payments,
 * the outlined button, Zelle's screen (`Zelle.tsx`) and the seam's words, the method named by its
 * `savedMethodWords` and `savedMethodNoun` (section 5 at 0b51e49, Q3). The words are in `words.ts`,
 * `SAVED_METHOD_PAYS`.
 *
 * - WHAT THE SAVED METHOD WILL DO, in a card: that it is saved, when it is charged while the charge
 *   waits on her answer, with the business's hours as ops pushes them, in the words of ops's
 *   `businessHoursWords`, and that a receipt is emailed
 *   only while ops says so. Once her face counts, "How did we do?" says what it means for the charge
 *   (`chargeReply`), and the card no longer says the charge waits on it. After Good it says nothing
 *   of the charge and the plain thanks stand: Good may name an area, which holds the charge until
 *   the office has put it right (ops's section 4 at 9f10a8b), and ops pushes no area.
 * - THE STAGE IS OPS'S (CLAUDE.md binding rule 1): its push, or, while the Worker carries her newest
 *   "I'm paying by Zelle" because the push has not caught up with it, what ops's receipt or its
 *   refusal said (`stageNow`, the paying spec's O10). `waiting` offers "I'm paying by Zelle" in the
 *   seam's words; `paying_by_zelle` is Zelle's screen with no other way, since ops refuses the bank
 *   and the card while the saved method pays, with "I sent it by Zelle" in it as ops's section 11.7
 *   says (`report`), and in its place what she was asked to send while her report holds the invoice
 *   on its way (`reported`, round five's slice B2); `pause_lifted` says the office lifted the pause, with
 *   the phone; `at_stripe` says it is being charged now, with the phone for a Zelle already sent. A
 *   stage this build does not know says only that the method is saved.
 * - "I'M PAYING BY ZELLE" sends `invoice.pay_by_zelle` through the request door (l.539). A key is
 *   minted once for a press, and kept only while the door could not be reached, so trying again names
 *   the same request; anything the door answers ends that press. While one is out another press does
 *   nothing. Sent says "Sent to the office." in place of the button, and nothing more until ops says
 *   so: while her ask is not yet with ops the invoice is read again, every three seconds and at most
 *   six times, and Zelle's screen is drawn once ops's receipt or push says she is paying by Zelle.
 * - WHAT OPS REFUSES IT WITH, whether the door says so from the copy or ops says so once the door
 *   has taken it, in the seam's words (`PAUSE_REFUSAL_WORDS`, section 5 at 0b51e49, Q2): the pause
 *   lifted and the charge at Stripe are those stages, as on ops's page; nothing owed now says its
 *   sentence in place of the button; the charge moved meanwhile says its sentence, reads again and
 *   offers the button, a new press being a new request; not charged automatically is the page's
 *   (`onNotCharged`), which says its sentence and draws the ways to pay; anything else says it did
 *   not go through, with the phone, and offers the button again.
 * - THE FOCUS goes to what a press brought: the line that says what came of it, the stage's
 *   sentence, Zelle's heading, or the button offered again once what ops answered says nothing, as
 *   the charge having moved does; never when the page is first drawn. The page is told of a press
 *   (`onPress`), so a refusal its read brings, that the saved method does not pay the invoice, takes
 *   the focus there too.
 * - FOLDED AWAY after a lesser face, as the ways to pay are: her Zelle, never what the saved method
 *   will do.
 * - ON PAYMENTS, the invoice's card says what the saved method will do in one line, in the same
 *   words shortened to fit a card (`savedMethodLine`), so she does not pay it again.
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import {
  sendRequest,
  type Invoice,
  type InvoiceAutopay,
  type InvoicePage,
  type PageAutopay,
  type TooMany,
  type ZelleAsk,
} from '../api.ts';
import { businessHoursWords } from '../lib/dates.ts';
import { belowGood, chargeFollows } from '../lib/faces.ts';
import {
  chargingNow,
  FACES,
  FAILED_BEFORE_PHONE,
  INVOICE,
  liftedAfterPhone,
  methodSaved,
  nothingCharged,
  PAUSE_REFUSAL_WORDS,
  PAYING,
  ratherByZelle,
  REQUEST,
  SAVED_METHOD_PAYS,
  thanksWeWillCharge,
  tooManyBeforePhone,
  weWillCharge,
  weWillChargeInHours,
  weWillChargeOnceTold,
  yourMethod,
  type Face,
} from '../words.ts';
import { GHOST_BUTTON } from './buttons.ts';
import { Folding } from './Folding.tsx';
import type { Signed } from './Guarded.tsx';
import { Phone } from './Phone.tsx';
import { ZelleWay, type ReportOffer } from './Zelle.tsx';

/** How long the page waits between reads while her ask is not yet with ops. */
export const PAUSE_POLL_GAP_MS = 3000;

/** How many times at most the invoice is read again while her ask is not yet with ops. */
export const PAUSE_POLLS = 6;

/** The stages of the saved method's charge the seam names (l.356). */
const STAGES = ['waiting', 'paying_by_zelle', 'pause_lifted', 'at_stripe'] as const;
export type Stage = (typeof STAGES)[number];

const stageOf = (word: string | undefined): Stage | null =>
  STAGES.find((stage) => stage === word) ?? null;

/** The refusals of "I'm paying by Zelle" that say the stage the charge is at (l.621). */
const REFUSED_AT: Readonly<Record<string, Stage>> = {
  pause_lifted: 'pause_lifted',
  charge_in_flight: 'at_stripe',
};

/** Ops's word for an invoice the saved method does not pay (l.621). */
const NOT_CHARGED = 'not_charged_automatically';

/** Ops's word for an invoice that owes nothing now (l.616). */
const NOT_OPEN = 'invoice_not_open';

/** Ops's word for a charge that moved while the pause was written (l.622). */
const CHARGE_CHANGED = 'charge_changed';

/** A face word ops sent, or null for one this build does not know. */
const faceOf = (word: string | undefined): Face | null =>
  FACES.find(({ face }) => face === word)?.face ?? null;

/**
 * The stage of the saved method's charge, as ops last said it: what ops answered her newest "I'm
 * paying by Zelle", while the Worker carries it because the push has not caught up with it, else
 * the push. Null for a stage this build does not know.
 */
export function stageNow(invoice: {
  readonly autopay?: InvoiceAutopay;
  readonly zelleAsk?: ZelleAsk;
}): Stage | null {
  const ask = invoice.zelleAsk;
  if (ask?.state === 'sent') {
    const said = stageOf(ask.stage);
    if (said !== null) return said;
  }
  if (ask?.state === 'refused' && ask.code !== undefined && ask.code in REFUSED_AT) {
    return REFUSED_AT[ask.code]!;
  }
  return stageOf(invoice.autopay?.stage);
}

/**
 * Whether ops last said a try has claimed the saved method's charge on the invoice: it is at Stripe
 * now (`stageNow`). A Stop cancels only the charges no try has claimed (the seam, l.540 at
 * b727ec1), so this one goes on, and its page and its card on Payments say it is being charged
 * until ops pushes the invoice as it stands, whatever ops has said of the method since.
 */
export const chargeClaimed = (invoice: {
  readonly autopay?: InvoiceAutopay;
  readonly zelleAsk?: ZelleAsk;
}): boolean => invoice.autopay !== undefined && stageNow(invoice) === 'at_stripe';

/** Ops answered her newest "I'm paying by Zelle" that the saved method does not pay the invoice. */
export const notChargedSaid = (invoice: Pick<InvoicePage, 'zelleAsk'>): boolean =>
  invoice.zelleAsk?.state === 'refused' && invoice.zelleAsk.code === NOT_CHARGED;

/** The business's hours in words, or null without them whole. */
const hoursOf = (autopay: PageAutopay) =>
  autopay.hours === undefined ? null : businessHoursWords(autopay.hours);

/**
 * What "How did we do?" says of the charge once a face counts, while it waits on her answer or the
 * office has lifted the pause: after Very good, thanks and when it is charged; after a lesser face,
 * that nothing is charged until it is put right (l.734-735). Null at any other stage, and for Good,
 * where the plain thanks stand: ops holds the charge after a good face that names an area (section 4
 * at 9f10a8b) and pushes no area, so home cannot say the charge comes (`chargeFollows`, round four,
 * slice F1).
 */
export function chargeReply(invoice: InvoicePage): ((face: Face) => string | null) | null {
  const { autopay } = invoice;
  const stage = stageNow(invoice);
  if (autopay === undefined || (stage !== 'waiting' && stage !== 'pause_lifted')) return null;
  const method = yourMethod(autopay.label, autopay.methodType);
  const hours = hoursOf(autopay);
  return (face) =>
    belowGood(face)
      ? nothingCharged(method)
      : chargeFollows(face)
        ? thanksWeWillCharge(method, hours)
        : null;
}

/**
 * What the card of an invoice the saved method pays says of it on Payments, so she does not pay it
 * again: what its own page says of the charge, shortened to fit a card, at the stage ops last pushed
 * and after the face ops says counts. While the charge waits on her answer, or the office has lifted
 * the pause, it is charged once she says the clean went well; once Very good counts, in business
 * hours; after a lesser face, nothing is charged until it is put right; and at Stripe, it is being
 * charged now. Null after Good, as its page thanks her plainly, since Good may name an area, which
 * holds the charge, and ops pushes no area (`chargeFollows`, round four, slice F1); and null where
 * the saved method does not pay it, while she pays it by Zelle, at a stage this build does not know,
 * and while a bank payment on it clears, which the card says instead.
 */
export function savedMethodLine(invoice: Invoice): string | null {
  const { autopay } = invoice;
  if (autopay === undefined || invoice.pay.clearing) return null;
  const method = yourMethod(autopay.label, autopay.methodType);
  const stage = stageNow({ autopay });
  if (stage === 'at_stripe') return chargingNow(method);
  if (stage !== 'waiting' && stage !== 'pause_lifted') return null;
  const face = faceOf(invoice.latestFace);
  if (face === null) return weWillChargeOnceTold(method);
  if (belowGood(face)) return nothingCharged(method);
  return chargeFollows(face) ? weWillChargeInHours(method) : null;
}

/** What the door answered her last press. */
type Answer =
  | { readonly kind: 'sent' }
  | { readonly kind: 'refused'; readonly code?: string }
  | { readonly kind: 'too_many'; readonly limit?: TooMany }
  | { readonly kind: 'failed' };

/** What the door answered, and the invoice drawn when it did. */
interface Press {
  readonly on: InvoicePage;
  readonly answer: Answer;
}

/**
 * The line under "I'm paying by Zelle": sent, it did not go through, nothing owed now, the invoice
 * changed while it was being sent, or past the request door's own limits (`429 too_many`).
 */
type Line = 'sent' | 'failed' | 'nothing' | 'changed' | 'too_many';

/** Whether "I'm paying by Zelle" is offered, and the line said under it. */
interface Offer {
  readonly offered: boolean;
  readonly line: Line | null;
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  readonly limit?: TooMany;
}

/** What a refusal says under "I'm paying by Zelle". */
function refusedWith(code: string | undefined): Offer {
  if (code === NOT_OPEN) return { offered: false, line: 'nothing' };
  if (code === CHARGE_CHANGED) return { offered: true, line: 'changed' };
  if (code === NOT_CHARGED || (code !== undefined && code in REFUSED_AT)) {
    return { offered: true, line: null };
  }
  return { offered: true, line: 'failed' };
}

interface Props {
  /**
   * The invoice, as ops last pushed it and as the page read it: the same object until it is read
   * again, which is how the page knows whether it has been read since the door answered.
   */
  readonly invoice: InvoicePage;
  /** The saved method paying it. */
  readonly autopay: PageAutopay;
  /** The household shown, which the request names. */
  readonly household: string;
  /** The face that counts: the one just sent, else ops's. */
  readonly answered: Face | null;
  /** She has opened her Zelle again after a lesser face folded it away. */
  readonly open: boolean;
  readonly onToggle: () => void;
  /** Ops says the saved method does not pay the invoice: the page draws the ways to pay. */
  readonly onNotCharged: () => void;
  /** She pressed "I'm paying by Zelle" here. */
  readonly onPress: () => void;
  /** The block, which takes the focus when it takes the place of a way to pay she pressed on. */
  readonly blockRef: Ref<HTMLDivElement>;
  /** "I sent it by Zelle" on Zelle's screen while she pays it by Zelle (ops's section 11.7). */
  readonly report?: ReportOffer;
  /**
   * What stands in place of Zelle's screen while her report holds the invoice on its way: what she
   * was asked to send, for her own report, or nothing; absent while nothing holds it.
   */
  readonly reported?: ReactNode;
  readonly signed: Signed;
  readonly reload: () => void;
}

export function SavedMethodPays({
  invoice,
  autopay,
  household,
  answered,
  open,
  onToggle,
  onNotCharged,
  onPress,
  blockRef,
  report,
  reported,
  signed,
  reload,
}: Props) {
  const { pay } = invoice;
  const [last, setLast] = useState<Press | null>(null);
  /** Counts the door's answers, so what each brought takes the focus. */
  const [said, setSaid] = useState(0);
  const [busy, setBusy] = useState(false);
  /** A press is out, so another does nothing. */
  const out = useRef(false);
  const mounted = useRef(true);
  /** She pressed here, so what comes of it takes the focus. */
  const pressedHere = useRef(false);
  /** The key minted for the press, kept only while the door could not be reached. */
  const minted = useRef<string | null>(null);
  /** How many times the invoice has been read again while her ask is not yet with ops. */
  const polls = useRef(0);
  /** The invoice drawn now, which a door's answer is about. */
  const drawn = useRef(invoice);
  drawn.current = invoice;
  const statusLine = useRef<HTMLParagraphElement>(null);
  const stageLine = useRef<HTMLParagraphElement>(null);
  const zelleHeading = useRef<HTMLHeadingElement>(null);
  const offerButton = useRef<HTMLButtonElement>(null);

  // No read since the door answered: what it said is the newest word.
  const fresh = last !== null && last.on === invoice;
  const local = last?.answer;
  // What ops answered her newest ask, unless the door has answered a press of hers since, which
  // wrote nothing new: an ask the door took is the Worker's to carry once it is read again.
  const asked = local === undefined || local.kind === 'sent' ? invoice.zelleAsk : undefined;
  const doorStage =
    fresh && local?.kind === 'refused' && local.code !== undefined && local.code in REFUSED_AT
      ? REFUSED_AT[local.code]!
      : null;
  const stage = doorStage ?? stageNow({ autopay, zelleAsk: asked });

  const method = yourMethod(autopay.label, autopay.methodType);
  const saved = methodSaved(autopay.methodType);
  const amountCents =
    pay.payable && !pay.clearing && invoice.balanceCents !== undefined && invoice.balanceCents > 0
      ? invoice.balanceCents
      : null;

  function offerNow(): Offer {
    if (local?.kind === 'sent' && fresh) return { offered: false, line: 'sent' };
    if (local?.kind === 'failed') return { offered: true, line: 'failed' };
    if (local?.kind === 'too_many') return { offered: true, line: 'too_many', limit: local.limit };
    if (local?.kind === 'refused') return refusedWith(local.code);
    switch (asked?.state) {
      case undefined:
        return { offered: true, line: null };
      case 'sending':
      case 'sent':
        return { offered: false, line: 'sent' };
      case 'failed':
        return { offered: true, line: 'failed' };
      case 'refused':
        return refusedWith(asked.code);
    }
  }
  const offer = offerNow();

  // Her ask is not yet with ops: read the invoice again, every three seconds, at most six times.
  const waiting = (fresh && local?.kind === 'sent') || asked?.state === 'sending';
  useEffect(() => {
    if (!waiting || polls.current >= PAUSE_POLLS) return;
    const timer = setTimeout(() => {
      polls.current += 1;
      reload();
    }, PAUSE_POLL_GAP_MS);
    return () => clearTimeout(timer);
  }, [invoice, waiting, reload]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // What a press brought takes the focus: Zelle's heading, the stage's sentence, the line, or, where
  // what ops answered says nothing, the button offered again.
  useEffect(() => {
    if (!pressedHere.current) return;
    if (stage === 'paying_by_zelle') zelleHeading.current?.focus();
    else if (stage === 'pause_lifted' || stage === 'at_stripe') stageLine.current?.focus();
    else (statusLine.current ?? offerButton.current)?.focus();
  }, [stage, said, offer.line]);

  function heard(answer: Answer) {
    setLast({ on: drawn.current, answer });
    setSaid((count) => count + 1);
  }

  async function press() {
    if (out.current) return;
    out.current = true;
    pressedHere.current = true;
    onPress();
    setBusy(true);
    minted.current ??= crypto.randomUUID();
    const answer = await sendRequest({
      key: minted.current,
      type: 'invoice.pay_by_zelle',
      household,
      payload: { invoice_id: invoice.id },
    });
    out.current = false;
    if (!mounted.current) return;
    setBusy(false);
    if (answer.outcome !== 'failed') minted.current = null;
    switch (answer.outcome) {
      case 'sent':
        polls.current = 0;
        heard({ kind: 'sent' });
        reload();
        return;
      case 'signed_out':
        signed.onSignedOut();
        return;
      case 'not_found':
        heard({ kind: 'failed' });
        reload();
        return;
      case 'refused':
        if (answer.code === NOT_CHARGED) {
          onNotCharged();
          return;
        }
        heard({ kind: 'refused', code: answer.code });
        if (refusedWith(answer.code).line !== 'failed') reload();
        return;
      case 'too_many':
        heard({ kind: 'too_many', limit: answer.limit });
        return;
      default:
        heard({ kind: 'failed' });
    }
  }

  // The card says what the saved method will do: that it is saved, when it is charged, and the
  // receipt. Zelle's screen stands in its place once she is paying by Zelle, and with nothing to say
  // there is no card.
  const charge =
    stage === 'at_stripe' ? (
      <p ref={stageLine} tabIndex={-1} className="text-body text-pretty text-text-body">
        {chargingNow(method)}
      </p>
    ) : (stage === 'waiting' || stage === 'pause_lifted') && answered === null ? (
      <p className="text-body text-pretty text-text-body">
        {weWillCharge(method, hoursOf(autopay))}
      </p>
    ) : null;
  const card =
    stage === 'paying_by_zelle' ? null : (
      <div className="flex flex-col gap-1 rounded-card border border-border-subtle bg-surface-raised px-5 py-4">
        <p className="text-body font-semibold text-pretty text-text-body">{saved}</p>
        {charge}
        {autopay.receiptByEmail ? (
          <p className="text-small text-pretty text-text-muted">{SAVED_METHOD_PAYS.receipt}</p>
        ) : null}
      </div>
    );

  // Her Zelle, which folds away after a lesser face: the offer while the charge waits, and Zelle's
  // screen once she is paying by Zelle, each only where ops says there is something to pay here.
  const canPause = amountCents !== null && pay.zelle !== undefined;
  const zelle =
    stage === 'waiting' && (canPause || offer.line === 'nothing') ? (
      <PayByZelleOffer
        canPause={canPause}
        methodType={autopay.methodType}
        offer={offer}
        busy={busy}
        onPress={() => void press()}
        lineRef={statusLine}
        buttonRef={offerButton}
      />
    ) : stage === 'paying_by_zelle' && amountCents !== null && reported === undefined ? (
      pay.zelle === undefined ? (
        <PhoneLine before={INVOICE.payBeforePhone} />
      ) : (
        <ZelleWay
          amountCents={amountCents}
          zelle={pay.zelle}
          report={report}
          headingRef={zelleHeading}
        />
      )
    ) : null;

  return (
    <div ref={blockRef} tabIndex={-1} data-saved-method-pays className="flex flex-col gap-5">
      {card}
      {zelle === null ? null : (
        <Folding folding={belowGood(answered)} open={open} onToggle={onToggle}>
          {zelle}
        </Folding>
      )}
      {/* What she was asked to send is not a way to pay, so it never folds away. */}
      {stage === 'paying_by_zelle' && amountCents !== null ? (reported ?? null) : null}
      {stage === 'pause_lifted' ? (
        <PhoneLine
          lineRef={stageLine}
          before={SAVED_METHOD_PAYS.liftedBeforePhone}
          rest={liftedAfterPhone(autopay.methodType)}
        />
      ) : stage === 'at_stripe' ? (
        <PhoneLine before={SAVED_METHOD_PAYS.alreadySentBeforePhone} />
      ) : null}
    </div>
  );
}

interface OfferProps {
  /** Ops says there is something to pay from here now, and sends a Zelle tag to pay it to. */
  readonly canPause: boolean;
  readonly methodType: string | undefined;
  readonly offer: Offer;
  /** A press is out. */
  readonly busy: boolean;
  readonly onPress: () => void;
  readonly lineRef: Ref<HTMLParagraphElement>;
  readonly buttonRef: Ref<HTMLButtonElement>;
}

/**
 * "Would you rather pay this one by Zelle?" with "I'm paying by Zelle", and under it, in a live
 * region, what came of a press: sent, it did not go through, or nothing to pay now, which is said
 * in place of the offer.
 */
function PayByZelleOffer({
  canPause,
  methodType,
  offer,
  busy,
  onPress,
  lineRef,
  buttonRef,
}: OfferProps) {
  const { line } = offer;
  const offering = canPause && line !== 'nothing';
  return (
    <div>
      {offering ? <p className="text-body text-pretty">{ratherByZelle(methodType)}</p> : null}
      {offering && offer.offered ? (
        <button
          ref={buttonRef}
          type="button"
          aria-disabled={busy ? true : undefined}
          onClick={onPress}
          className={`mt-4 cursor-pointer aria-disabled:cursor-wait aria-disabled:opacity-70 ${GHOST_BUTTON}`}
        >
          {SAVED_METHOD_PAYS.payingByZelle}
        </button>
      ) : null}
      <div aria-live="polite">
        {line === null ? null : (
          <p
            ref={lineRef}
            tabIndex={-1}
            className={`text-body text-pretty ${offering ? 'mt-3' : ''}`}
          >
            {line === 'sent' ? (
              REQUEST.sent
            ) : line === 'nothing' ? (
              PAYING.nothingToPay
            ) : line === 'changed' ? (
              PAUSE_REFUSAL_WORDS.charge_changed
            ) : (
              <>
                {line === 'too_many' ? tooManyBeforePhone(offer.limit) : FAILED_BEFORE_PHONE}{' '}
                <Phone after="." />
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

interface PhoneLineProps {
  readonly before: string;
  /** The rest of the sentence after the phone, after a space; with none, a full stop ends it. */
  readonly rest?: string;
  readonly lineRef?: Ref<HTMLParagraphElement>;
}

/** A sentence with the office's number in it, as a link that calls it. */
function PhoneLine({ before, rest, lineRef }: PhoneLineProps) {
  return (
    <p
      ref={lineRef}
      tabIndex={lineRef === undefined ? undefined : -1}
      className="text-body text-pretty"
    >
      {before}{' '}
      {rest === undefined ? (
        <Phone after="." />
      ) : (
        <>
          <Phone /> {rest}
        </>
      )}
    </p>
  );
}
